import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';

import { AppPagination } from '@/__core/components';
import DataTableModule from '@/__core/layout/module/DataTableModule';
import { sharedListStyles } from '@/__core/styles/sharedListStyles';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';

import { getReceipts } from '../redux/ReceiptCRUD';
import { InventoryReceiptRow } from '../types';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const resolveListCompanyId = (companyId?: string) => {
  const resolved = (companyId || getCurrentCompanyId() || '').trim();
  if (!resolved || resolved === 'all' || resolved === 'global' || !UUID_PATTERN.test(resolved)) {
    return undefined;
  }
  return resolved;
};

type ReceiptListPagination = {
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

export type ReceiptListHandle = {
  requestRefresh: () => void;
};

interface ReceiptListProps {
  companyId?: string;
  searchQuery?: string;
  status?: string;
  onApplyBulkAction?: (action: 'approve' | 'delete', rows: InventoryReceiptRow[]) => Promise<void> | void;
  bulkActionLoading?: boolean;
  onSelectRow?: (row: InventoryReceiptRow) => void;
  canPost?: boolean;
  canDelete?: boolean;
}

const resolveStatus = (row: InventoryReceiptRow): { code: string; name: string; color: string } => {
  if (typeof row.status === 'object' && row.status !== null) {
    return {
      code: row.status.code || row.status_code || '',
      name: row.status.name || row.status_display || '',
      color: row.status.color || '#6C757D',
    };
  }
  return {
    code: row.status_code || row.status || '',
    name: row.status_display || row.status || '',
    color: row.status_color || '#6C757D',
  };
};

const formatReceivedDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB');
};

const ReceiptList = forwardRef<ReceiptListHandle, ReceiptListProps>(function ReceiptList(
  {
    companyId,
    searchQuery = '',
    status = 'all',
    onApplyBulkAction,
    bulkActionLoading = false,
    onSelectRow,
    canPost = true,
    canDelete = true,
  },
  ref
) {
  const [rows, setRows] = useState<InventoryReceiptRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [pagination, setPagination] = useState<ReceiptListPagination>({
    count: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 1,
  });
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState('');
  const canBulkSelect = canPost || canDelete;

  const resolvedCompanyId = useMemo(() => resolveListCompanyId(companyId), [companyId]);

  useEffect(() => {
    setPagination((current) => ({ ...current, page: 1 }));
  }, [resolvedCompanyId, searchQuery, status]);

  useEffect(() => {
    let active = true;

    const fetchReceipts = async () => {
      setLoading(true);
      try {
        const response = await getReceipts({
          companyId: resolvedCompanyId,
          search: searchQuery || undefined,
          status: status !== 'all' ? status : undefined,
          page: pagination.page,
          pageSize: pagination.pageSize,
        });

        if (!active) return;

        setRows(response.results || []);
        setPagination({
          count: response.count || 0,
          page: response.page || pagination.page,
          pageSize: response.pageSize || pagination.pageSize,
          totalPages: response.totalPages || 1,
        });
      } catch (_error) {
        if (!active) return;
        setRows([]);
        setPagination((current) => ({ ...current, count: 0, totalPages: 1 }));
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    const timeout = window.setTimeout(() => {
      void fetchReceipts();
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [resolvedCompanyId, searchQuery, status, pagination.page, pagination.pageSize, reloadTick]);

  useEffect(() => {
    const existingIds = new Set(rows.map((row) => row.id));
    setSelectedRowIds((current) => current.filter((id) => existingIds.has(id)));
  }, [rows]);

  useImperativeHandle(
    ref,
    () => ({
      requestRefresh: () => setReloadTick((current) => current + 1),
    }),
    []
  );

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setPagination((current) => ({ ...current, page }));
  };

  const selectedRows = useMemo(
    () => rows.filter((row) => selectedRowIds.includes(row.id)),
    [rows, selectedRowIds]
  );

  const handleToggleRow = (id: string) => {
    if (!canBulkSelect) {
      return;
    }
    setSelectedRowIds((current) => (current.includes(id) ? current.filter((v) => v !== id) : [...current, id]));
  };

  const handleToggleSelectAll = () => {
    if (!canBulkSelect) {
      return;
    }
    setSelectedRowIds((current) => {
      const allSelected = rows.length > 0 && current.length === rows.length;
      if (allSelected) {
        return [];
      }
      return rows.map((row) => row.id);
    });
  };

  const handleBulkActionChange = (event: SelectChangeEvent<string>) => {
    const nextAction = event.target.value;
    if (nextAction === 'approve' && !canPost) {
      return;
    }
    if (nextAction === 'delete' && !canDelete) {
      return;
    }
    setBulkAction(nextAction);
  };

  const handleApplyBulkAction = () => {
    if (!bulkAction || selectedRows.length === 0) {
      return;
    }

    if (bulkAction === 'delete' && !canDelete) {
      return;
    }
    if (bulkAction === 'approve' && !canPost) {
      return;
    }

    void Promise.resolve(onApplyBulkAction?.(bulkAction as 'approve' | 'delete', selectedRows)).then(() => {
      setBulkAction('');
      setSelectedRowIds([]);
    });
  };

  const totalCount = pagination?.count || rows.length;
  const currentPage = pagination?.page || 1;
  const pageSize = pagination?.pageSize || rows.length || DEFAULT_PAGE_SIZE;
  const totalPages = pagination?.totalPages || 1;

  const areAllRowsSelected = useMemo(
    () => rows.length > 0 && selectedRowIds.length === rows.length,
    [rows.length, selectedRowIds.length]
  );
  const areSomeRowsSelected = useMemo(
    () => selectedRowIds.length > 0 && selectedRowIds.length < rows.length,
    [rows.length, selectedRowIds.length]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1, height: 220 }}>
        <Typography variant='body1' color='text.secondary'>Không có phiếu nhập kho</Typography>
        <Typography variant='body2' color='text.secondary'>Thử thay đổi bộ lọc hoặc tạo phiếu mới.</Typography>
      </Box>
    );
  }

  const columns: Array<{
    id: string;
    label: string;
    width?: string;
    align?: 'left' | 'center' | 'right';
    render?: (_: unknown, row: InventoryReceiptRow) => React.ReactNode;
  }> = [
    {
      id: 'receipt_no',
      label: 'Mã phiếu nhập',
      width: '170px',
      render: (_: unknown, row: InventoryReceiptRow) => <Typography sx={{ fontWeight: 600 }}>{row.receipt_no || '—'}</Typography>,
    },
    { id: 'receipt_type_display', label: 'Loại nhập', width: '160px' },
    { id: 'supplier_name', label: 'Nhà cung cấp', width: '240px' },
    { id: 'warehouse_name', label: 'Kho nhập', width: '200px' },
    { id: 'purchase_no', label: 'PO', width: '160px' },
    {
      id: 'status',
      label: 'Trạng thái',
      width: '160px',
      render: (_: unknown, row: InventoryReceiptRow) => {
        const st = resolveStatus(row);
        return <Typography sx={{ fontWeight: 600, color: st.color }}>{st.name || '—'}</Typography>;
      },
    },
    {
      id: 'received_at',
      label: 'Thời điểm nhận',
      width: '180px',
      render: (_: unknown, row: InventoryReceiptRow) => formatReceivedDate(row.received_at),
    },
  ];

  return (
    <Box sx={sharedListStyles.container}>
      <DataTableModule
        data={rows}
        columns={columns}
        leftFilterWidth={0}
        onRowSelect={handleToggleRow}
        onSelectAll={handleToggleSelectAll}
        onRowClick={(row) => onSelectRow?.(row as InventoryReceiptRow)}
        getRowId={(row) => (row as InventoryReceiptRow).id}
        isRowSelected={(row) => selectedRowIds.includes((row as InventoryReceiptRow).id)}
        areAllRowsSelected={areAllRowsSelected}
        areSomeRowsSelected={areSomeRowsSelected}
        showCheckbox={canBulkSelect}
      />

      {selectedRowIds.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            px: 2,
            py: 2,
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, flexDirection: { xs: 'column', md: 'row' }, gap: 1 }}>
            <Typography sx={{ fontSize: '0.875rem', color: '#374151' }}>
              Đã chọn {selectedRowIds.length} phiếu
            </Typography>
            <FormControl size='small' sx={{ minWidth: 180 }}>
              <Select value={bulkAction} displayEmpty onChange={handleBulkActionChange}>
                <MenuItem value='' disabled>
                  Chọn thao tác
                </MenuItem>
                {canPost ? <MenuItem value='approve'>Duyệt</MenuItem> : null}
                {canDelete ? <MenuItem value='delete'>Xóa</MenuItem> : null}
              </Select>
            </FormControl>
            <Button variant='contained' size='small' onClick={handleApplyBulkAction} disabled={!bulkAction || bulkActionLoading}>
              {bulkActionLoading ? 'Đang xử lý...' : 'Áp dụng'}
            </Button>
          </Box>
        </Box>
      )}

      <AppPagination
        count={totalCount}
        page={currentPage}
        pageSize={pageSize}
        totalPages={totalPages}
        itemLabel='phiếu nhập'
        onPageChange={handlePageChange}
      />
    </Box>
  );
});

export default ReceiptList;
